// src/context/AuthContext.js
import React, { createContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "../services/api";
import { getToken, clearAuth } from "../services/authStorage"; // <- use session-only storage

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);          // normalized: { id, username, role, isVerified, kycApproved, createdAt }
  const [kycSubmitted, setKycSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  const normalizeUser = (me) => {
    if (!me) return null;
    return {
      id: me.id,
      username: me.username,
      role: me.role,
      isVerified: me.is_verified ?? me.isVerified ?? false,
      kycApproved: me.kyc_approved ?? me.kycApproved ?? false,
      createdAt: me.created_at ?? me.createdAt ?? null,
      firstName: me.first_name ?? me.firstName ?? '',
      lastName: me.last_name ?? me.lastName ?? '',
      middleName: me.middle_name ?? me.middleName ?? '',
      phoneNumber: me.phone_number ?? me.phoneNumber ?? '',
      dateOfBirth: me.date_of_birth ?? me.dateOfBirth ?? null,
      bankName: me.bank_name ?? me.bankName ?? '',
      bankAccountName: me.bank_account_name ?? me.bankAccountName ?? '',
      bankAccountNumber: me.bank_account_number ?? me.bankAccountNumber ?? '',
      bvn: me.bvn ?? me.BVN ?? '',
      adminNote: me.admin_note ?? me.adminNote ?? '',
    };
  };

  const refreshUser = useCallback(async (options = {}) => {
    const resolvedOptions = typeof options === "boolean" ? { silent: options } : options || {};
    const { silent = false } = resolvedOptions;
    const token = getToken();

    if (!token) {
      setUser(null);
      setKycSubmitted(false);
      if (!silent) {
        setLoading(false);
      }
      return null;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      const me = await api.fetchCurrentUser(); // axios instance with bearer already attached
      const normalized = normalizeUser(me);
      setUser(normalized);
      setKycSubmitted(Boolean(normalized?.kycApproved));
      return normalized;
    } catch (err) {
      console.error("Error loading user:", err);
      const hasToken = Boolean(getToken());
      if (!silent || !hasToken) {
        // If the session is invalid/expired, or this was an explicit refresh, clear it
        clearAuth();
        setUser(null);
        setKycSubmitted(false);
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const token = getToken();
    if (!token || user?.kycApproved) return;

    let cancelled = false;
    let inFlight = false;

    const maybeRefresh = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        await refreshUser({ silent: true });
      } catch (err) {
        console.error("Background refresh failed", err);
      } finally {
        inFlight = false;
      }
    };

    maybeRefresh();
    const intervalId = setInterval(maybeRefresh, 15000);

    const handleVisibility = () => {
      if (typeof document !== "undefined" && !document.hidden) {
        maybeRefresh();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
    }

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
    };
  }, [user?.kycApproved, refreshUser]);

  const isAdmin = useMemo(() => user?.role === "admin", [user]);

  const logout = () => {
    clearAuth();
    setUser(null);
    setKycSubmitted(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, isAdmin, kycSubmitted, setKycSubmitted, loading, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};
